import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { broadcastTo } from "../../data/ledgerWrites.js";

// Debug logging - set to true to enable verbose logging
const DEBUG_LOGGING = false;
const debugLog = (...args) => DEBUG_LOGGING && console.log(...args);

// Create a context to hold the registry
const TransactionHookRegistryContext = createContext({
  registerHook: () => {},
  unregisterHook: () => {},
  getRegisteredHooks: () => [],
  broadcast: () => 0,
});

// Create a provider component that manages the registry
export const TransactionHookRegistryProvider = ({ children }) => {
  // Use a ref to store the registry to ensure it persists between renders
  const registryRef = useRef(new Set());

  // Function to unregister a hook
  const unregisterHook = useCallback((hook) => {
    debugLog("[TransactionRegistry] Unregistering hook");
    registryRef.current.delete(hook);
  }, []);

  // Function to register a hook
  const registerHook = useCallback(
    (hook) => {
      debugLog(
        "[TransactionRegistry] Registering hook with methods:",
        Object.keys(hook).filter((key) => typeof hook[key] === "function"),
      );
      registryRef.current.add(hook);
      return () => unregisterHook(hook);
    },
    [unregisterHook],
  );

  // Function to get all registered hooks
  const getRegisteredHooks = useCallback(() => {
    return Array.from(registryRef.current);
  }, []);

  // The bus: call `method(...args)` on every registered handle that has it.
  // The write doors (src/data/ledgerWrites.js) and the reconciliation hook
  // drive the registry through this instead of each hand-rolling the loop.
  const broadcast = useCallback(
    (method, ...args) => broadcastTo(registryRef.current)(method, ...args),
    [],
  );

  // The context value is memoized (plan §3.4): it used to be rebuilt on every
  // provider render, so every consumer whose effect depended on `registerHook`
  // (useRegisterAccountTransactionsHook below, and the Postgres adapters) tore
  // its registration down and re-registered on each render of the provider.
  // The three functions are stable, so the value is too.
  const contextValue = useMemo(
    () => ({
      registerHook,
      unregisterHook,
      getRegisteredHooks,
      broadcast,
    }),
    [registerHook, unregisterHook, getRegisteredHooks, broadcast],
  );

  return (
    <TransactionHookRegistryContext.Provider value={contextValue}>
      {children}
    </TransactionHookRegistryContext.Provider>
  );
};

// Hook for components to access the registry
export const useAccountTransactionsRegistry = () => {
  return useContext(TransactionHookRegistryContext);
};

// Hook for transaction hooks to register themselves with the registry
export const useRegisterAccountTransactionsHook = (hook) => {
  const { registerHook } = useAccountTransactionsRegistry();

  useEffect(() => {
    // Register the hook when the component mounts
    const unregister = registerHook(hook);

    // Unregister the hook when the component unmounts
    return unregister;
  }, [hook, registerHook]);
};
